/**
 * @module experience/device-assessment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/*tslint:disable:max-file-line-count */
/* tslint:disable:no-any */ // Need to support all data types
// Core libraries
import { Injectable, EventEmitter } from '@angular/core';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { Wove } from 'aspect.js/';
// services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { RemorseService } from '@support/cart-support/services/remorse.service';
// interfaces
import {
  NavigationItem,
  RemorseExchangeDetails,
  ProductsCount,
  DeviceAssessmentConfigData,
  SelectedAssessmentQuestions,
  ValidateDeviceContent,
  ManagerOverrideDetails,
} from '@experience/device-assessment/interfaces/device-assessment.interface';
import { APIError } from '@support/api/common/error.interface';
import { ASSISTED_API_MAP } from '@core/api/proxies/assisted-api-endpoints';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { CartResponse } from '@support/api/proxies/responses/api-cart-response.interface';

// constants
import {
  DEVICE_ASSESSSMENT_CONSTANT,
  CART_METHOD,
} from '@experience/device-assessment/device-assessment.constants';
import { DeviceAssessmentQuestionsResponse } from '@support/api/proxies/responses/api-240.interface';
import {
  OrderLine,
  SelectedItems,
  AssessmentItem,
  ReturnReason,
} from '@experience/order-summary/interfaces/order-summary-new.interface';
import { Reasons } from '@experience/store-operations/interfaces/store-ops.interface';
import { CartRequestPayloadModel } from '@experience/device-assessment/model/assess-item.model';
import { Product } from '@support/api/proxies/responses/api-2814-new.interface';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { HttpResponse, HttpHeaders } from '@angular/common/http';

@Wove({ moduleName: DEVICE_ASSESSSMENT_CONSTANT.DEVICE_ASSESSMENT_MODULE_NAME })
@Injectable()
export class DeviceAssessmentService {
  /**
   *  deviceDiagnosticValue is of type String
   */
  private deviceDiagnosticValue: string;

  /**
   *  remorseDetails is of type RemorseExchangeDetails
   */
  private remorseDetails: RemorseExchangeDetails;

  /**
   *  remorseAssessmentType is of type String
   */
  private remorseAssessmentType: string;

  /**
   *  selectedProduct is of type Item
   */
  private selectedProduct: Product;

  /**
   *  map selected product and cartItemID
   */
  private mappedSelectedProduct: Map<string, string>;

  /**
   * variable saves left panel data
   */
  private leftPanelData: NavigationItem[];

  /**
   * Event emitter used to emit an event on nav panel navigation
   */
  public assessmentNavigationUpdated$: Observable<boolean>;

  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  public deviceAssessmentUpdatedSource: Subject<boolean>;

  /**
   * Event emitter used to emit an event on tab value change
   */
  public exchangeUpdated$: Observable<boolean>;

  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  public warrantyExchangeUpdated: Subject<boolean>;

  /**
   * Event emitter used to emit an event on nav panel navigation
   */
  public multiItemsAssessment$: Observable<boolean>;
  /**
   * Stores value of diagnostic values
   */
  private selectedValue: string;
  /**
   * Stores value of imei
   */
  private imei: string;
  /**
   * stores questions of assessment
   */
  private deviceAssessmentQuestions: SelectedAssessmentQuestions[];
  /**
   * stores reason which is selected
   */
  private selectedReason: string;

  /**
   * Sets the flag to true if its wrranty flow
   */
  private isWarranty: boolean;
  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  public multiItemRemorseAssessmentSource: Subject<boolean>;

  /**
   * Stores API error message
   */
  public errorMessage: APIError;

  constructor(
    private apiGateway: ApiGatewayService,
    private contentProviderService: ContentProviderService,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private commonUtilService: CommonUtilService,
    private formCreationService: FormCreationService,
    private commonApiService: CommonApiService,
    private remorseService: RemorseService,
    private formBuilder: UntypedFormBuilder
  ) {
    this.deviceAssessmentUpdatedSource = new Subject();
    this.assessmentNavigationUpdated$ = this.deviceAssessmentUpdatedSource.asObservable();
    this.warrantyExchangeUpdated = new Subject();
    this.exchangeUpdated$ = this.warrantyExchangeUpdated.asObservable();
    this.multiItemRemorseAssessmentSource = new Subject();
    this.multiItemsAssessment$ = this.multiItemRemorseAssessmentSource.asObservable();
    this.mappedSelectedProduct = new Map();
    this.deviceAssessmentQuestions = [];
    this.selectedReason = null;
  }

  /**
   * Method for getting AEM content data
   * @returns Observable<DeviceAssessmentConfigData>
   */
  public getDeviceAssessmentContentData(endpoint?: string): Observable<DeviceAssessmentConfigData> {
    return this.contentProviderService.getContentData(endpoint).pipe(
      map((contentData: DeviceAssessmentConfigData) => {
        return contentData;
      }),
      catchError((error: APIError[]) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method for creating left panel
   * @param content: SummaryDetailsContent object
   * @returns NavigationItem[]
   */
  public createLeftPanel(
    content: DeviceAssessmentConfigData,
    navForDevice: boolean,
    isWarranty?: boolean
  ): NavigationItem[] {
    const leftPanelOptions = [];
    if (content) {
      const orderSummary: NavigationItem = {
        label: content.orderSummary,
        disabled: true,
        selected: false,
        route: '/device-assessment/summary',
      };
      if (!isWarranty) {
        leftPanelOptions.push(orderSummary);
      }
      const validateDevice: NavigationItem = {
        label: content.validateDevice,
        disabled: !isWarranty,
        selected: isWarranty,
        route: '/device-assessment/validate-device',
      };
      const assessItem: NavigationItem = {
        label: content.assessItem,
        disabled: true,
        selected: false,
        route: '/device-assessment/assess-item',
      };
      const runDiagnostics: NavigationItem = {
        label: content.runDiagnostics,
        disabled: true,
        selected: false,
        route: '/device-assessment/run-diagnostic',
      };
      if (navForDevice) {
        leftPanelOptions.push(validateDevice);
        leftPanelOptions.push(assessItem);
        leftPanelOptions.push(runDiagnostics);
      }
      const selectAReason: NavigationItem = {
        label: content.selectAReason,
        disabled: true,
        selected: false,
        route: '/device-assessment/select-reason',
      };
      leftPanelOptions.push(selectAReason);
    }
    return leftPanelOptions;
  }

  /**
   * Used to get remorseDetails
   * @return remorseDetails
   */
  public getRemorseDetails(): RemorseExchangeDetails {
    return this.remorseDetails;
  }

  /**
   * Used to get selected product
   * @return ProductOffering
   */
  public getSelectedProductItem(): Product {
    return this.selectedProduct;
  }

  /**
   * Used to get imei
   * @return string
   */
  public getImei(): string {
    return this.imei;
  }

  /**
   * Used to set imei value
   * @return void
   * @param imei
   */
  public setImei(imei: string): void {
    this.imei = imei;
  }

  /**
   * Used to get selected questions
   * @return SelectedAssessmentQuestions
   */
  public getSelectedQuestions(): SelectedAssessmentQuestions[] {
    return this.deviceAssessmentQuestions;
  }

  /**
   * Used to set imei value
   * @return void
   * @param imei
   */
  public setSelectedQuestions(questions: SelectedAssessmentQuestions): void {
    if (this.deviceAssessmentQuestions.length === DEVICE_ASSESSSMENT_CONSTANT.THREE) {
      this.deviceAssessmentQuestions.forEach(
        (selectedQuestion: SelectedAssessmentQuestions, index: number) => {
          if (selectedQuestion.questionId === questions.questionId) {
            this.deviceAssessmentQuestions.splice(index, 1);
            this.deviceAssessmentQuestions.splice(index, 0, questions);
          }
        }
      );
    } else {
      this.deviceAssessmentQuestions.push(questions);
    }
  }

  /**
   * Used to selected value
   * @return string
   */
  public getSelectedValue(): string {
    return this.selectedValue;
  }

  /**
   * Used to set selected
   * @return void
   * @param value
   */
  public setSelectedValue(value: string): void {
    this.selectedValue = value;
  }
  /**
   * used to clear saved data
   */
  public clearDetails(item?: number): void {
    const isWarrantyExchange =
      this.stateStoreUtilsService.getDeviceAssessmentType() ===
      DEVICE_ASSESSSMENT_CONSTANT.WARRANTY;
    switch (item) {
      case isWarrantyExchange ? DEVICE_ASSESSSMENT_CONSTANT.ZERO : DEVICE_ASSESSSMENT_CONSTANT.ONE:
        this.deviceAssessmentQuestions = [];
        this.selectedReason = null;
        this.selectedValue = null;
        break;
      case isWarrantyExchange ? DEVICE_ASSESSSMENT_CONSTANT.ONE : DEVICE_ASSESSSMENT_CONSTANT.Two:
        this.selectedValue = null;
        this.selectedReason = null;
        break;
      case isWarrantyExchange ? DEVICE_ASSESSSMENT_CONSTANT.Two : DEVICE_ASSESSSMENT_CONSTANT.THREE:
        this.selectedReason = null;
        break;
      default:
        this.selectedValue = null;
        this.deviceAssessmentQuestions = [];
        this.selectedReason = null;
        this.imei = null;
    }
  }
  /**
   * Used to set assessment details
   */
  public setAssessmentDetails(): void {
    let accessory;
    let devices;
    let lineDevices;
    let epinData;
    let d2AData;
    const items = this.stateStoreUtilsService.getSelectedProductForReturn();
    if (this.commonUtilService.isNotEmpty(items)) {
      devices = this.updateAssessProducts(items.devices);
      lineDevices = this.findNotAssessedLineDevices(items.line);
      accessory = this.updateAssessProducts(items.accessory);
      epinData = this.updateAssessProducts(items.ePinData);
      d2AData = this.updateAssessProducts(items.directToAccountData);
      if (this.commonUtilService.isNotEmpty(lineDevices)) {
        this.setRemorseAssessmentType(
          DEVICE_ASSESSSMENT_CONSTANT.LINE_DEVICE,
          this.objectService.get(lineDevices, '0')
        );
      } else if (this.commonUtilService.isNotEmpty(devices)) {
        const nonLineDevice = this.objectService.get(devices, '0');
        this.setRemorseAssessmentType(DEVICE_ASSESSSMENT_CONSTANT.DEVICE, nonLineDevice);
      } else if (this.commonUtilService.isNotEmpty(accessory)) {
        this.setRemorseAssessmentType(
          DEVICE_ASSESSSMENT_CONSTANT.ACCESSORY,
          this.objectService.get(accessory, '0')
        );
      } else if (this.commonUtilService.isNotEmpty(epinData)) {
        this.setRemorseAssessmentType(
          DEVICE_ASSESSSMENT_CONSTANT.EPIN,
          this.objectService.get(epinData, '0')
        );
      } else {
        this.setRemorseAssessmentType(
          DEVICE_ASSESSSMENT_CONSTANT.D2A,
          this.objectService.get(d2AData, '0')
        );
      }
    }
  }

  /**
   * method to fetch count information of products available
   * @param ProductsCount
   */
  public getProductsCountInfo(): ProductsCount {
    let products = [];
    let currentItemCount = 1;
    let productName;
    let productType;
    const productCount: ProductsCount = <ProductsCount>new Object();
    const selectedItems = this.stateStoreUtilsService.getSelectedProductForReturn();
    if (this.commonUtilService.isNotEmpty(selectedItems)) {
      const lineDevices = this.findLineDevices(selectedItems.line);
      this.remorseService.setAssessmentProductsMapping(selectedItems);
      products = [
        ...lineDevices,
        ...selectedItems.devices,
        ...selectedItems.accessory,
        ...selectedItems.ePinData,
        ...selectedItems.directToAccountData,
      ];

      const productLength = products.length;
      for (let index: number = 0; index < productLength; index++) {
        productName = products[index].offerName;
        productType = products[index].productType;
        if (!products[index].assessmentComplete) {
          currentItemCount = index + 1;
          break;
        }
      }
    }
    productCount.productName = productName;
    productCount.totalItemCount = products.length;
    productCount.currentItemPosition = currentItemCount;
    return productCount;
  }
  /**
   * method to set reason for warranty replacement
   * @param reason: string
   */
  public setWarrantyReplacementReasons(reason: string): void {
    this.stateStoreUtilsService.setWarrantyReplacementReasons(reason);
  }

  /**
   * Method should update assessment complete for the selected items for return
   */
  public updateProductAssessmentComplete(): void {
    const items = this.stateStoreUtilsService.getSelectedProductForReturn();
    this.updateAssessmentComplete(items.devices);
    this.updateAssessmentCompleteForLineItems(items.line);
    this.updateAssessmentComplete(items.accessory);
    this.updateAssessmentComplete(items.ePinData);
    this.stateStoreUtilsService.setSelectedProductForReturn(items);
  }

  /**
   * Used to set Assessment type
   * @param type : string
   * @param product : Product
   */
  public setRemorseAssessmentType(type: string, product: Product): void {
    this.remorseAssessmentType = type;
    this.selectedProduct = product;
  }

  /**
   * Used to get Assessment type
   * @return string
   */
  public getRemorseAssessmentType(): string {
    return this.remorseAssessmentType;
  }

  /**
   * Used to set Assessment type
   * @param type : string
   * @param product : ProductOffering
   */
  public setAssessmentBladeData(data: NavigationItem[]): void {
    this.leftPanelData = data;
  }

  /**
   * Used to get Assessment type
   * @return string
   */
  public getAssessmentBladeData(): NavigationItem[] {
    return this.leftPanelData;
  }

  /**
   * Method to create a formGroup for supervisor form
   * @returns FormGroup
   */
  public buildSupervisorForm(): UntypedFormGroup {
    const fieldValidationConstants = this.getFieldValidationConstants();
    return this.formBuilder.group({
      ntId: this.formCreationService.getFormControl(fieldValidationConstants.NTID, '', true),
      password: this.formCreationService.getFormControl(
        fieldValidationConstants.TMO_PASSWORD,
        '',
        true
      ),
    });
  }

  /**
   * Method to get device assesment questions
   * @returns ObjectInterface
   */
  public getDeviceAssessmentQuestions(): Observable<DeviceAssessmentQuestionsResponse> {
    const returnMode = this.stateStoreUtilsService.getDeviceAssessmentType();
    let queryParams = {
      type: '',
      returnMode: '',
    };
    if (returnMode === DEVICE_ASSESSSMENT_CONSTANT.WARRANTY) {
      queryParams = {
        type: DEVICE_ASSESSSMENT_CONSTANT.EXCHANGE,
        returnMode: returnMode,
      };
    } else {
      queryParams = {
        type: returnMode.toLocaleUpperCase(),
        returnMode: DEVICE_ASSESSSMENT_CONSTANT.REMORSE,
      };
    }
    return this.apiGateway
      .getRequest(API_MAP.API_240_getDeviceAssessmentQuestions, null, queryParams, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method is used to create request payload and post data to API 2128 and 2129
   * @param deviceAssessmentQuestions : deviceAssessmentQuestions
   * @return Observable<CartResponse>
   */
  public handleAssessItemDataToCart(
    deviceAssessmentQuestions: SelectedAssessmentQuestions[],
    returnReason?: ReturnReason,
    id?: string,
    skuVal?: string,
    managerOverrideDetails?: ManagerOverrideDetails,
    overrideIndicator?: boolean
  ): Observable<CartResponse> {
    const assessmentType = this.stateStoreUtilsService.getDeviceAssessmentType();
    this.isWarranty = assessmentType === DEVICE_ASSESSSMENT_CONSTANT.WARRANTY;
    const cartId = this.stateStoreUtilsService.getCartId();
    const productId = this.objectService.get(this.getSelectedProductItem(), `id`);
    this.remorseService.updateAssessmentProductsMapWithAnswers(
      productId,
      deviceAssessmentQuestions
    );
    if (this.selectedProduct && cartId !== DEVICE_ASSESSSMENT_CONSTANT.CART_ID) {
      return this.putDeviceAssessmentDataToCart(
        deviceAssessmentQuestions,
        returnReason,
        id,
        skuVal,
        managerOverrideDetails,
        overrideIndicator
      );
    } else {
      return this.postDeviceAssessmentDataToCart(
        deviceAssessmentQuestions,
        returnReason,
        managerOverrideDetails,
        overrideIndicator
      );
    }
  }
  /**
   * Method is used to create request payload and post data to API 2128 and 2129
   * @param deviceAssessmentQuestions : deviceAssessmentQuestions
   * @return Observable<CartResponse>
   */
  public postDeviceAssessmentDataToCart(
    deviceAssessmentQuestions?: SelectedAssessmentQuestions[],
    returnReason?: ReturnReason,
    managerOverrideDetails?: ManagerOverrideDetails,
    overrideIndicator?: boolean
  ): Observable<CartResponse> {
    let requestPayload;
    requestPayload = this.createPayload(
      deviceAssessmentQuestions,
      returnReason,
      null,
      null,
      managerOverrideDetails,
      CART_METHOD.POST
    );
    const url = requestPayload.apiUrl;
    const pathParams = requestPayload.apiPathParams;
    if (!this.isWarranty) {
      requestPayload = requestPayload.requestPayloadRemorseExchange;
    } else {
      requestPayload = requestPayload.requestPayloadWarrantyExchange;
    }
    let headers = new HttpHeaders();
    if (overrideIndicator) {
      headers = headers.append('overrideIndicator', overrideIndicator.toString());
    }
    return this.apiGateway.postRequest(url, requestPayload, null, null, pathParams).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError[]) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method is used to create request payload and post data to API 2128 and 2129
   * @param deviceAssessmentQuestions : deviceAssessmentQuestions
   * @return Observable<CartResponse>
   */
  public putDeviceAssessmentDataToCart(
    deviceAssessmentQuestions?: SelectedAssessmentQuestions[],
    returnReason?: ReturnReason,
    id?: string,
    skuVal?: string,
    managerOverrideDetails?: ManagerOverrideDetails,
    overrideIndicator?: boolean
  ): Observable<CartResponse> {
    let requestPayload;
    requestPayload = this.createPayload(
      deviceAssessmentQuestions,
      returnReason,
      id,
      skuVal,
      managerOverrideDetails,
      CART_METHOD.PUT
    );
    const assessmentType = this.stateStoreUtilsService.getDeviceAssessmentType();
    const url = requestPayload.apiUrl;
    const pathParams = requestPayload.apiPathParams;
    if (assessmentType !== DEVICE_ASSESSSMENT_CONSTANT.WARRANTY) {
      requestPayload = requestPayload.requestPayloadRemorseExchange;
    } else {
      requestPayload = requestPayload.requestPayloadWarrantyExchange;
    }
    let headers = new HttpHeaders();
    if (overrideIndicator) {
      headers = headers.append('overrideIndicator', overrideIndicator.toString());
    }
    return this.apiGateway.putRequest(url, requestPayload, headers, null, pathParams).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError[]) => {
        return observableThrowError(error);
      })
    );
  }
  /**
   * method to create payload for create a cart or update cart
   *
   */
  private createPayload(
    deviceAssessmentQuestions?: SelectedAssessmentQuestions[],
    returnReason?: ReturnReason,
    id?: string,
    skuVal?: string,
    managerOverrideDetails?: ManagerOverrideDetails,
    methodType?: string
  ): CartRequestPayloadModel {
    this.selectedProduct = this.getSelectedProductItem();
    const selectedProductId = !this.isWarranty
      ? this.objectService.get(this.selectedProduct, 'id')
      : '';
    const assessmentProductMap: AssessmentItem = !this.isWarranty
      ? this.remorseService.getAssessmentProductsMapping(selectedProductId)
      : null;
    return new CartRequestPayloadModel(
      methodType,
      assessmentProductMap,
      this.stateStoreUtilsService.getSelectedProductForWarrantyExchange(),
      this.selectedProduct,
      deviceAssessmentQuestions,
      this.stateStoreUtilsService,
      this.objectService,
      this.objectService.get(returnReason, 'reason'),
      id,
      skuVal,
      null,
      this.objectService.get(returnReason, 'selectUnsellableReason'),
      this.objectService.get(returnReason, 'unsellableReasonDescription'),
      this.objectService.get(returnReason, 'isItemSellable'),
      this.objectService.get(returnReason, 'licensePlate'),
      managerOverrideDetails
    );
  }

  /**
   * method to handle get API 326 call for fetching select reasons
   */
  public getSelectReasons(): Observable<Reasons> {
    let reasonType;
    const selectedItems: SelectedItems = this.stateStoreUtilsService.getSelectedProductForReturn();
    const assessementType = this.stateStoreUtilsService.getDeviceAssessmentType();
    if (
      this.objectService.get(selectedItems, 'type') ===
        DEVICE_ASSESSSMENT_CONSTANT.REMORSE_RETURN_SELECTED_TYPE &&
      assessementType === DEVICE_ASSESSSMENT_CONSTANT.REMORSE_RETURN_SELECTED_TYPE
    ) {
      reasonType = DEVICE_ASSESSSMENT_CONSTANT.RETURN_REASON;
    } else {
      reasonType = DEVICE_ASSESSSMENT_CONSTANT.EXCHANGE_REASON;
    }
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    return this.apiGateway
      .getRequest(
        ASSISTED_API_MAP.API_326_getAdjustmentReasons,
        null,
        { type: reasonType },
        cacheObj
      )
      .pipe(
        map((response: HttpResponse<Reasons>) => {
          return response.body;
        })
      );
  }

  /**
   * Method to create a formGroup for full imei field
   * @returns FormGroup
   */

  public buildFullImeiForm(): UntypedFormGroup {
    const fieldValidationConstants = this.getFieldValidationConstants();
    return this.formBuilder.group({
      imei: this.formCreationService.getFormControl(fieldValidationConstants.IMEI, '', true),
    });
  }

  /**
   * Method to get field validation constants
   * @returns ObjectInterface
   */

  public getFieldValidationConstants(): ObjectInterface {
    return this.commonApiService.getFieldValidationConstants();
  }

  /**
   * Method used to set cart response in statestoreutil
   * @param cartResponse : CartResponse
   */
  public setCartResponse(cartResponse: CartResponse): void {
    this.stateStoreUtilsService.setCart(cartResponse.cart);
  }

  /**
   * Method used to set cart response in statestoreutil
   * @param cartResponse : CartResponse
   */
  public cleanupOnDestroy(): void {
    this.deviceDiagnosticValue = null;
    this.remorseDetails = null;
    this.remorseAssessmentType = null;
    this.selectedProduct = null;
    this.mappedSelectedProduct.clear();
    this.leftPanelData = null;
  }

  /**
   * To check permission
   * @param url, abf
   * @returns boolean
   */
  public getPermissionABFValue(url: string, abf: string): boolean {
    const permission = this.stateStoreUtilsService.getPermissionABFValue(url, abf);
    return permission.toLowerCase() === DEVICE_ASSESSSMENT_CONSTANT.ENABLE;
  }

  /**
   * Creats Static Modal for RIS Invetory Check
   * @returns void
   */
  public createRISInventoryModal(validateDeviceContent?: ValidateDeviceContent): StaticModal {
    const risInventoryModal: StaticModal = {
      headerText: validateDeviceContent.ohNoNotification,
      bodyText: validateDeviceContent.ohNoNotificationMessage,
      disableDismiss: true,
      modalName: 'ris-inventory-modal',
      disableEsc: true,
      buttons: [
        {
          id: validateDeviceContent.goToOrderSummary,
          type: DEVICE_ASSESSSMENT_CONSTANT.SUBMIT,
          name: validateDeviceContent.goToOrderSummary,
          class: DEVICE_ASSESSSMENT_CONSTANT.PRIMARY_BUTTON,
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
    return risInventoryModal;
  }

  /**
   * Method should update assessment complete for the item for which assessment step are completed
   * @param products
   */
  private updateAssessmentComplete(products: Product[]): void {
    const selectedItem = this.getSelectedProductItem();
    const productsLength = products.length;
    for (let index: number = 0; index < productsLength; index++) {
      if (products[index].id === selectedItem.id) {
        products[index].assessmentComplete = true;
      }
    }
  }

  /**
   * Method should update assessment complete for line items
   * @param lines
   */
  private updateAssessmentCompleteForLineItems(lines: OrderLine[]): void {
    const selectedItem = this.getSelectedProductItem();
    lines.forEach((line: OrderLine) => {
      if (line.devices.id === selectedItem.id) {
        line.devices.assessmentComplete = true;
      }
    });
  }

  /**
   * method to update products which are assesed
   * @param products
   * @returns Product
   */
  private updateAssessProducts(products: Product[]): Product[] {
    let item;
    if (this.commonUtilService.isNotEmpty(products)) {
      item = products.filter((product: Product) => {
        return !product.assessmentComplete;
      });
    }
    return item;
  }

  /**
   * method to find line products which are not assesed
   * @param lines
   * @returns Product
   */
  private findNotAssessedLineDevices(lines: OrderLine[]): Product[] {
    const item = [];
    if (this.commonUtilService.isNotEmpty(lines)) {
      lines.forEach((line: OrderLine) => {
        if (!line.devices.assessmentComplete) {
          item.push(line.devices);
        }
      });
    }
    return item;
  }

  /**
   * method to find line products
   * @param lines
   * @returns Product
   */
  private findLineDevices(lines: OrderLine[]): Product[] {
    const item = [];
    if (this.commonUtilService.isNotEmpty(lines)) {
      lines.forEach((line: OrderLine) => {
        if (line.devices) {
          item.push(line.devices);
        }
      });
    }
    return item;
  }
  /**
   * method to handle get API 2044 call for fetching unsellable reasons
   */
  public getUnsellableReasons(): Observable<Reasons> {
    const reasonType = DEVICE_ASSESSSMENT_CONSTANT.UNSELLABLE_REASON;
    const cacheObj: ApiCache = { preferCache: true, cacheResponse: true };
    return this.apiGateway
      .getRequest(ASSISTED_API_MAP.API_2044_getReasons, null, { type: reasonType }, cacheObj)
      .pipe(
        map((response: HttpResponse<Reasons>) => {
          return response.body;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  public createModal(): StaticModal {
    const mixedCartModal: StaticModal = {
      headerText: '',
      bodyText: '',
      disableDismiss: true,
      disableEsc: true,
      buttons: [
        {
          id: '',
          type: 'submit',
          name: '',
          class: 'btn btn-primary',
        },
        {
          id: 'ViewCart',
          type: 'cancel',
          name: '',
          class: 'btn btn-secondary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
    return mixedCartModal;
  }
}
